/**
 * PropertySearchFilterToolbar - Core Component
 * 
 * SCORABLE component - theme-agnostic toolbar for property search/filter/sort
 * Provides structure and logic, themes customize styling via className
 */

'use client';

import { SearchBar } from '../filters/SearchBar';
import { SortControl, type SortOption } from '../filters/SortControl';

export interface BasicFilter {
  id: string;
  label: string;
  type: 'select' | 'multi-select' | 'range';
  options?: { value: string; label: string }[];
  value?: string | string[] | { min?: number; max?: number };
  onChange: (value: string | string[] | { min?: number; max?: number }) => void;
}

export interface PropertySearchFilterToolbarProps<T = unknown> {
  /**
   * Search configuration
   */
  search: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
  };
  /**
   * Sort configuration
   */
  sort: {
    label: string;
    options: SortOption<T>[];
    value: string;
    onChange: (value: string) => void;
  };
  /**
   * Basic filters (bedrooms, bathrooms, price) - rendered inline
   */
  basicFilters?: BasicFilter[];
  /**
   * Advanced filters button click handler
   */
  onAdvancedFiltersClick?: () => void;
  /**
   * Advanced filters button label
   */
  advancedFiltersLabel?: string;
  /**
   * Results count
   */
  resultsCount?: number;
  /**
   * View mode controls (grid/list/map)
   */
  viewControls?: React.ReactNode;
  /**
   * Custom className for container
   */
  className?: string;
  /**
   * Custom className for toolbar sections
   */
  toolbarClassName?: string;
  /**
   * Render function for basic filters (allows theme customization)
   */
  renderBasicFilters?: (filters: BasicFilter[]) => React.ReactNode;
  /**
   * Render function for advanced filters button (allows theme customization)
   */
  renderAdvancedButton?: (onClick: () => void, label: string) => React.ReactNode;
  /**
   * Render function for results count (allows theme customization)
   */
  renderResultsCount?: (count: number) => React.ReactNode;
}

/**
 * Core Property Search Filter Toolbar
 * 
 * Combines search, basic filters, sort, and view controls into a toolbar.
 * Themes can customize all aspects via className and render props.
 */
export function PropertySearchFilterToolbar<T = unknown>({
  search,
  sort,
  basicFilters = [],
  onAdvancedFiltersClick,
  advancedFiltersLabel = 'Advanced Filters',
  resultsCount,
  viewControls,
  className = '',
  toolbarClassName = '',
  renderBasicFilters,
  renderAdvancedButton,
  renderResultsCount,
}: PropertySearchFilterToolbarProps<T>) {
  return (
    <div className={className}>
      {/* Row 1: Search + View Controls */}
      <div className={`flex flex-col sm:flex-row gap-4 mb-4 ${toolbarClassName}`}>
        <div className="flex-1">
          <SearchBar
            placeholder={search.placeholder}
            value={search.value}
            onChange={search.onChange}
            className="w-full"
          />
        </div>
        {viewControls && <div>{viewControls}</div>}
      </div>

      {/* Row 2: Basic Filters + Advanced Button */}
      {(basicFilters.length > 0 || onAdvancedFiltersClick) && (
        <div className={`flex flex-col lg:flex-row lg:items-center gap-4 mb-4 ${toolbarClassName}`}>
          {/* Basic Filters */}
          {basicFilters.length > 0 && (
            <div className="flex-1">
              {renderBasicFilters ? (
                renderBasicFilters(basicFilters)
              ) : (
                <div className="flex flex-wrap gap-3">
                  {basicFilters.map((filter) => (
                    <div key={filter.id} className="text-sm text-gray-600">
                      {filter.label}: {JSON.stringify(filter.value)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Advanced Filters Button */}
          {onAdvancedFiltersClick && (
            <div>
              {renderAdvancedButton ? (
                renderAdvancedButton(onAdvancedFiltersClick, advancedFiltersLabel)
              ) : (
                <button
                  onClick={onAdvancedFiltersClick}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {advancedFiltersLabel}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Row 3: Sort + Results Count */}
      <div className={`flex flex-col sm:flex-row sm:items-center gap-4 ${toolbarClassName}`}>
        <div className="flex-1">
          <SortControl
            options={sort.options}
            value={sort.value}
            onChange={sort.onChange}
            label={sort.label}
          />
        </div>

        {resultsCount !== undefined && (
          <div>
            {renderResultsCount ? (
              renderResultsCount(resultsCount)
            ) : (
              <div className="text-sm text-gray-600">
                {resultsCount} {resultsCount === 1 ? 'property' : 'properties'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

